import { useState, useRef } from 'react';
import { UserPlus } from 'phosphor-react';

interface User {
  email: string;
  role: 'member' | 'admin';
}

interface UserListProps {
  users: User[];
  onAddUser: (email: string, role: 'member' | 'admin') => void;
  onRemoveUser: (email: string) => void;
  onError?: (error: string | null) => void;
}

export function UserList({ users, onAddUser, onRemoveUser, onError }: UserListProps) {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'member' | 'admin'>('member');

  const handleAddUser = () => {
    if (!newUserEmail.trim()) {
      onError?.('Por favor, insira um email válido');
      return;
    }

    if (users.some((u) => u.email === newUserEmail.trim())) {
      onError?.('Este usuário já foi adicionado');
      return;
    }

    onAddUser(newUserEmail.trim(), newUserRole);
    setNewUserEmail('');
    emailInputRef.current?.focus();
  };

  return (
    <div className="space-y-6">
      {/* Add User Form */}
      <div className="flex gap-2 mb-4">
        <input
          ref={emailInputRef}
          type="email"
          value={newUserEmail}
          onChange={(e) => {
            setNewUserEmail(e.target.value);
            onError?.(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newUserEmail.trim()) {
              handleAddUser();
            }
          }}
          placeholder="email@exemplo.com"
          className="flex-1 h-10 px-4 border rounded-xl focus:outline-none focus:border-[color:var(--brand-500)] focus:ring-2 focus:ring-[color:var(--brand-500)]/40 transition-all duration-200 theme-input theme-text placeholder:text-[color:var(--foreground-muted)]/60 placeholder:text-sm text-sm"
        />
        <select
          value={newUserRole}
          onChange={(e) => setNewUserRole(e.target.value as 'member' | 'admin')}
          className="h-10 px-4 text-sm border rounded-xl focus:outline-none focus:border-[color:var(--brand-500)] focus:ring-2 focus:ring-[color:var(--brand-500)]/40 transition-all duration-200 theme-input theme-text appearance-none cursor-pointer"
        >
          <option value="member">Membro</option>
          <option value="admin">Admin</option>
        </select>
        <button
          type="button"
          onClick={handleAddUser}
          className="h-10 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer bg-[color:var(--nt-primary)] text-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover)] flex items-center gap-2"
        >
          <UserPlus className="w-5 h-5" />
          Adicionar
        </button>
      </div>

      {/* Users List */}
      {users.length > 0 && (
        <div className="space-y-2">
          {users.map((user, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 rounded-lg border theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_50%,transparent)]"
            >
              <div>
                <div className="text-sm font-medium theme-text">{user.email}</div>
                <div className="text-xs theme-text-muted">{user.role === 'admin' ? 'Admin' : 'Membro'}</div>
              </div>
              <button
                type="button"
                onClick={() => onRemoveUser(user.email)}
                className="text-red-500 hover:text-red-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
